import { Airframe } from "../types/airframe";
import { Component, PendingComponentSubAssembly } from "../types/component";
import { SubAssembly } from "../types/subAssembly";
import { getComponentTypeName } from "./componentTypeUtils";

type SubAssemblySerialSource = Pick<
  PendingComponentSubAssembly,
  "serialNumber" | "subAssemblyType"
>;

export type ComponentSubAssemblyAttachInfo = {
  landingGearType: string;
};

/** Internal blob folder for detached sub-assemblies — not a selectable assembly. */
export const LANDING_GEAR_SUB_ASSEMBLY_POOL_FOLDER = "SubAssembly";

export function isLandingGearSubAssemblyPoolFolder(value: string): boolean {
  return value.trim().localeCompare(LANDING_GEAR_SUB_ASSEMBLY_POOL_FOLDER, undefined, {
    sensitivity: "accent",
  }) === 0;
}

const SUB_ASSEMBLY_TYPE_ORDER = [
  "SHOCK STRUT",
  "SIDE STRUT",
  "2ND SIDE STRUT",
  "SIDESTAY",
  "RETRACTION ACTUATOR",
  "DRAG BRACE",
  "AUX DRAG",
];

function compareSubAssemblyTypes(left: string, right: string): number {
  const leftIndex = SUB_ASSEMBLY_TYPE_ORDER.indexOf(left.trim().toUpperCase());
  const rightIndex = SUB_ASSEMBLY_TYPE_ORDER.indexOf(right.trim().toUpperCase());

  if (leftIndex !== -1 && rightIndex !== -1) {
    return leftIndex - rightIndex;
  }

  if (leftIndex !== -1) return -1;
  if (rightIndex !== -1) return 1;

  return left.localeCompare(right);
}

/** Builds a landing gear serial from attached sub-assembly serial numbers. */
export function buildLandingGearSerialFromSubAssemblies(
  subAssemblies: SubAssemblySerialSource[]
): string {
  if (!subAssemblies.length) return "";

  return [...subAssemblies]
    .sort((left, right) =>
      compareSubAssemblyTypes(left.subAssemblyType, right.subAssemblyType)
    )
    .map((item) => item.serialNumber.trim())
    .filter(Boolean)
    .join("-");
}

export function validateSubAssemblyTypeUniqueness(
  items: Array<Pick<SubAssemblySerialSource, "subAssemblyType">>
): string | null {
  const seen = new Set<string>();

  for (const item of items) {
    const normalized = item.subAssemblyType.trim().toUpperCase();
    if (!normalized) {
      continue;
    }

    if (seen.has(normalized)) {
      return `Each sub-assembly type can only be attached once. '${item.subAssemblyType}' is already attached.`;
    }

    seen.add(normalized);
  }

  return null;
}

export function parseComponentCustomMetadata(
  component: Pick<Component, "customMetadata" | "customFields">
): Record<string, unknown> {
  if (component.customMetadata && Object.keys(component.customMetadata).length > 0) {
    return component.customMetadata;
  }

  if (!component.customFields?.trim()) {
    return {};
  }

  try {
    const parsed = JSON.parse(component.customFields.trim());
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    return {};
  }

  return {};
}

export function isLandingGearSlotType(componentType: unknown): boolean {
  return getComponentTypeName(componentType as number | string | undefined) === "Landing Gear";
}

export function componentHasAttachedSubAssemblies(
  component: Component,
  attachInfo?: ComponentSubAssemblyAttachInfo
): boolean {
  if (attachInfo) {
    return true;
  }

  return landingGearHasSubAssembly(parseComponentCustomMetadata(component));
}

export function isLandingGearComponent(component: Component): boolean {
  const typeCandidates = [
    getComponentTypeName(component.componentType),
    getComponentTypeName(component.type),
    String(component.type ?? "").trim(),
    String(component.componentType ?? "").trim(),
  ].filter(Boolean);

  return typeCandidates.some(
    (typeName) =>
      typeName === "Landing Gear" ||
      typeName === "LandingGear" ||
      typeName === "2"
  );
}

export type RestrictedLandingGearFamily = "CRJ" | "EMBRAER";

export function normalizeRestrictedLandingGearFamily(
  value: unknown
): RestrictedLandingGearFamily | null {
  const normalized = String(value ?? "").trim().toUpperCase();
  if (!normalized) return null;

  const isCrjFamily =
    normalized.includes("CRJ") ||
    normalized.includes("CANADIAN REGIONAL JET") ||
    normalized.includes("BOMBARDIER") ||
    normalized === "MHI" ||
    normalized.includes("MITSUBISHI HEAVY INDUSTRIAL");

  if (isCrjFamily) return "CRJ";

  if (normalized.includes("EMBRAER") || normalized.includes("EMBARER")) {
    return "EMBRAER";
  }

  return null;
}

export function resolveComponentLandingGearFamily(
  component: Pick<Component, "customMetadata" | "customFields">
): RestrictedLandingGearFamily | null {
  const metadata = parseComponentCustomMetadata(component);
  return normalizeRestrictedLandingGearFamily(metadata.lgAirframeFamily);
}

/**
 * Landing gear can attach when its stored airframe family matches the airframe
 * manufacturer family (CRJ / Embraer aliases). For other manufacturers, the
 * stored family must match the manufacturer name so restricted-family gear
 * (e.g. Embraer) is not offered on unrelated airframes (e.g. Indian Jet).
 */
export function isLandingGearCompatibleWithAirframe(
  component: Component,
  airframeManufacturer: string | null | undefined
): boolean {
  if (!isLandingGearComponent(component)) {
    return true;
  }

  const manufacturer = String(airframeManufacturer ?? "").trim();
  if (!manufacturer) {
    return true;
  }

  const metadata = parseComponentCustomMetadata(component);
  const storedFamily = String(metadata.lgAirframeFamily ?? "").trim();
  const airframeFamily = normalizeRestrictedLandingGearFamily(manufacturer);
  const componentFamily = normalizeRestrictedLandingGearFamily(storedFamily);

  if (airframeFamily) {
    return componentFamily === airframeFamily;
  }

  // Other manufacturers (e.g. Indian Jet) can take normal landing gear.
  // Only CRJ/Embraer family gear is excluded.
  return componentFamily == null;
}

/**
 * Returns true when the airframe manufacturer is CRJ or Embraer
 * (including CRJ aliases such as Bombardier, MHI, and Canadian Regional Jet).
 * Those families attach sub-assembly landing gear; other manufacturers do not.
 */
export function airframeSupportsLandingGearSubAssemblies(
  airframe: Airframe | null | undefined
): boolean {
  return Boolean(normalizeRestrictedLandingGearFamily(airframe?.manufacturer));
}

/** Derives the landing gear position (e.g. RMLG) from attached sub-assemblies. */
export function resolveLandingGearPositionFromSubAssemblies(
  subAssemblies: SubAssembly[]
): string | null {
  if (!subAssemblies.length) return null;

  const landingGearType = subAssemblies[0]?.landingGearType?.trim();
  return landingGearType || null;
}

export async function loadLandingGearSubAssemblyAttachInfo(
  componentId: string,
  component: Component,
  getComponentSubAssemblies: (id: string) => Promise<SubAssembly[]>
): Promise<ComponentSubAssemblyAttachInfo | null> {
  const metadata = parseComponentCustomMetadata(component);
  const metadataHasSubAssembly = landingGearHasSubAssembly(metadata);

  try {
    const subAssemblies = await getComponentSubAssemblies(componentId);
    if (subAssemblies.length > 0) {
      const landingGearType =
        resolveLandingGearPositionFromSubAssemblies(subAssemblies) ?? "";
      return { landingGearType };
    }

    return metadataHasSubAssembly ? { landingGearType: "" } : null;
  } catch {
    return metadataHasSubAssembly ? { landingGearType: "" } : null;
  }
}

function metadataIndicatesSubAssembly(
  metadata?: Record<string, unknown> | null
): boolean {
  if (!metadata) {
    return false;
  }

  const value =
    metadata.hasSubAssembly ??
    metadata.HasSubAssembly ??
    metadata.lgHasSubAssembly ??
    metadata.LgHasSubAssembly;

  return value === true || value === "true" || value === "Yes";
}

export function landingGearHasSubAssembly(
  metadata?: Record<string, unknown> | null,
  hasSubAssemblyFlag?: boolean
): boolean {
  if (hasSubAssemblyFlag === true) {
    return true;
  }

  if (metadataIndicatesSubAssembly(metadata)) {
    return true;
  }

  return false;
}

export type PreparedComponentMetadata = Record<string, unknown> & {
  offWingType?: string;
  shopVisitReasonId?: string;
  shopVisitReasonLabel?: string;
  lgHasSubAssembly?: string;
};

export function clearOffWingFromComponentMetadata(
  metadata: Record<string, unknown> | null | undefined
): PreparedComponentMetadata {
  return {
    ...(metadata ?? {}),
    offWingType: "",
    OffWingType: "",
    OffWingStatus: "",
    shopVisitReasonId: "",
    ShopVisitReasonId: "",
    shopVisitReasonLabel: "",
    ShopVisitReasonLabel: "",
  };
}

export function prepareComponentMetadataForSave(
  metadata: Record<string, unknown> | null | undefined,
  options: { isLandingGear: boolean; hasSubAssembly: boolean }
): PreparedComponentMetadata {
  const base = { ...(metadata ?? {}) };

  if (options.isLandingGear) {
    base.lgHasSubAssembly = options.hasSubAssembly ? "Yes" : "No";
  }

  if (options.isLandingGear && options.hasSubAssembly) {
    return clearOffWingFromComponentMetadata(base);
  }

  return base;
}

export function findSlotIdByLocationCode(
  slots: Array<{ id: string; locationCode?: string | null }>,
  locationCode: string
): string | null {
  const normalized = locationCode.trim().toUpperCase();
  if (!normalized) return null;

  const match = slots.find(
    (slot) => (slot.locationCode?.trim().toUpperCase() ?? "") === normalized
  );

  return match?.id ?? null;
}
